import * as Sentry from '@sentry/react';
import { useState } from 'react';

import {
  Entertainer,
  EntertainerFeaturedCta,
  EntertainerSocialLink,
  useCurrentEntertainerQuery,
} from '@queries/EntertainerQueries';
import { FeedEvent } from '@queries/EventQueries';

import { useUpdateEntertainerMutation } from '@mutations/EntertainerMutations';
import { useDynamicUpdateEventMutation, useUpdateEventMutation } from '@mutations/EventMutations';

import Button from '@components/buttons/Button';
import { FormLabel } from '@components/form/FormLabel';
import { Input } from '@components/form/Input';
import { Select } from '@components/form/Select';
import { Popup } from '@components/layout/Popup/Popup';
import { PrimaryCtaCard } from '@components/layout/PrimaryCtaCard/PrimaryCtaCard';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { EntertainerScheduleDateDetail } from '@screens/entertainer/DragKingCommunityScreen/ScheduledSocialPost';
import { LinksListField } from '@screens/entertainer/components/LinksListField';

interface AddEventExternalLinkPopupContentProps {
  onClose: () => void;
  externalLinkShow: FeedEvent | EntertainerScheduleDateDetail;
}

const isFeedEvent = (
  externalLinkShow: FeedEvent | EntertainerScheduleDateDetail,
): externalLinkShow is FeedEvent => {
  return !!(externalLinkShow as any)?._id;
};

const AddEventExternalLinkPopupContent = ({
  externalLinkShow,
  onClose,
}: AddEventExternalLinkPopupContentProps) => {
  const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();
  const { isSubmitting, updateEventAsync } = useDynamicUpdateEventMutation();

  const [externalLinkButton, setExternalLinkButton] = useState<{ text?: string; url: string }>({
    text: 'Tickets',
    url: '',
  });

  const handleSubmit = async () => {
    try {
      if (isFeedEvent(externalLinkShow)) {
        await updateEventAsync({ eventId: externalLinkShow._id, externalLinkButton });
      }

      onClose();
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
    }
  };

  const canSubmit = externalLinkButton.text && externalLinkButton.url;

  return (
    <div>
      <TitleToolbar text="Add show link" size="md" className="mb-4" />
      <div>
        <div className="d-flex gap-2 w-100">
          <div style={{ width: 200 }}>
            <FormLabel text="Link Text" />
            <Select
              defaultValue={externalLinkButton.text}
              onChange={(e) => {
                setExternalLinkButton({ ...externalLinkButton, text: e.target.value });
              }}
              options={['Tickets', 'Reservations', 'Details'].map((option) => ({
                label: option,
                value: option,
              }))}
            />
          </div>
          <div className="w-100">
            <FormLabel text="Link URL" />
            <div className="d-flex gap-2 align-items-center">
              <div className="w-100">
                <Input
                  type="text"
                  placeholder="eg. https://www.example.com"
                  defaultValue={externalLinkButton.url}
                  onChange={(e) => {
                    setExternalLinkButton({ ...externalLinkButton, url: e.target.value });
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4">
        <Button disabled={!canSubmit || isSubmitting} roundness="rounded" onClick={handleSubmit}>
          Save link
        </Button>
      </div>
    </div>
  );
};

interface AddEventExternalLinkPopupProps extends Omit<
  AddEventExternalLinkPopupContentProps,
  'currentEntertainer' | 'externalLinkShow'
> {
  isVisible: boolean;
  externalLinkShow: FeedEvent | EntertainerScheduleDateDetail | undefined;
}

export const AddEventExternalLinkPopup = ({
  isVisible,
  externalLinkShow,
  onClose,
}: AddEventExternalLinkPopupProps) => {
  return (
    <Popup isVisible={isVisible} onClose={onClose}>
      {isVisible && externalLinkShow && (
        <Popup.Content>
          <AddEventExternalLinkPopupContent externalLinkShow={externalLinkShow} onClose={onClose} />
        </Popup.Content>
      )}
    </Popup>
  );
};
